import { createContext, ReactNode, useContext } from 'react';
import { apiV2 } from '../services/api';

type EntityId = string | number;

type ApiResponse<T = any> = {
  data?: T;
  message?: string;
  status?: number;
  success?: boolean;
};

export interface ProductionAdvance {
  id_production_advance: EntityId;
  employee: {
    id_employee: EntityId;
    full_name: string;
  };
  id_order: EntityId;
  original_amount: number;
  allocated_amount: number;
  available_amount: number;
  is_available: boolean;
  order_status: string[];
  order_is_active: boolean;
  due_date?: string | null;
  payment_date?: string | null;
  payment_type: {
    id_paymenttype: string;
    paymenttype_name: string;
  };
  notes?: string;
  created_at: string;
  actions?: {
    canEdit: boolean;
    canCancel: boolean;
    canPay: boolean;
    canReversePayment: boolean;
  };
}

export interface ProductionAdvanceDetails extends ProductionAdvance {
  updated_at: string;
  allocations: Array<{
    amount: number;
    allocated_at: string;
    id_trip: EntityId;
    id_generation: EntityId;
    id_order: EntityId;
    order_status: string[];
    order_is_active: boolean;
    created_at?: string;
  }>;
}

export type ProductionAdvanceInput = {
  employee_id: EntityId;
  amount: number;
  due_date: string;
  notes: string;
};

export type ProductionAdvanceUpdateInput = Omit<ProductionAdvanceInput, 'employee_id'>;

export type ProductionAdvancePaymentInput = {
  payment_date: string;
  payment_type_id: string;
};

export interface ProductionAdvancesFilter {
  employee_id?: EntityId;
  trip_id?: EntityId;
  only_available?: boolean;
}

export interface ProductionAdvancesProviderResponse<T = undefined> {
  success: boolean;
  message: string;
  data?: T;
}

interface ProductionAdvancesProviderProps {
  children: ReactNode;
}

interface ProductionAdvancesProviderContextData {
  getProductionAdvances: (filter?: ProductionAdvancesFilter) => Promise<ProductionAdvancesProviderResponse<ProductionAdvance[]>>;
  getProductionAdvance: (advanceId: EntityId) => Promise<ProductionAdvancesProviderResponse<ProductionAdvanceDetails>>;
  createProductionAdvance: (advance: ProductionAdvanceInput) => Promise<ProductionAdvancesProviderResponse<ProductionAdvance>>;
  updateProductionAdvance: (advanceId: EntityId, advance: ProductionAdvanceUpdateInput) => Promise<ProductionAdvancesProviderResponse<ProductionAdvance>>;
  cancelProductionAdvance: (advanceId: EntityId) => Promise<ProductionAdvancesProviderResponse>;
  payProductionAdvance: (advanceId: EntityId, payment: ProductionAdvancePaymentInput) => Promise<ProductionAdvancesProviderResponse>;
  reverseProductionAdvancePayment: (advanceId: EntityId) => Promise<ProductionAdvancesProviderResponse>;
}

const ProductionAdvancesProviderContext = createContext<ProductionAdvancesProviderContextData>(
  {} as ProductionAdvancesProviderContextData
);

export const productionAdvancesRoutes = {
  advances: '/production-advances',
  advance: (advanceId: EntityId) => `/production-advances/${advanceId}`,
  pay: (advanceId: EntityId) => `/production-advances/${advanceId}/pay`,
  reversePayment: (advanceId: EntityId) => `/production-advances/${advanceId}/reverse-payment`
};

function isTechnicalMessage(message: unknown) {
  if (typeof message !== 'string') return true;

  return /^(cannot\s+(get|post|put|patch|delete)|axioserror|error:|network error|internal server error|failed with status code)/i.test(message.trim()) ||
    /<\/?[a-z][\s\S]*>/i.test(message) ||
    /\/api\/v\d/i.test(message);
}

function translateKnownApiMessage(message: unknown) {
  if (typeof message !== 'string') return undefined;

  const normalizedMessage = message.trim();

  if (/^employee not found\.?$/i.test(normalizedMessage)) {
    return 'Colaborador não encontrado ou inativo.';
  }

  if (/^active cost category produção was not found for this company\.?$/i.test(normalizedMessage)) {
    return 'A categoria de custo PRODUÇÃO precisa estar ativa para cadastrar adiantamentos.';
  }

  if (/^active payment type dinheiro was not found for this company\.?$/i.test(normalizedMessage)) {
    return 'O tipo de pagamento Dinheiro precisa estar ativo para cadastrar adiantamentos.';
  }

  if (/^production advance not found\.?$/i.test(normalizedMessage)) {
    return 'Adiantamento de produção não encontrado.';
  }

  if (/^target trip with a begin date was not found\.?$/i.test(normalizedMessage)) {
    return 'A viagem precisa ter uma data de início para consultar os adiantamentos disponíveis.';
  }

  if (/^a production advance with allocations cannot be (changed or )?cancelled\.?$/i.test(normalizedMessage)) {
    return 'Este adiantamento já possui abatimentos e não pode ser alterado ou removido.';
  }

  if (/^only a pending production advance can be (changed or )?cancelled\.?$/i.test(normalizedMessage)) {
    return 'Apenas adiantamentos pendentes podem ser alterados ou removidos.';
  }

  if (/^payment type is inactive or does not belong to this company\.?$/i.test(normalizedMessage)) {
    return 'O tipo de pagamento selecionado não está disponível.';
  }

  if (/^only an active pending production advance can be paid\.?$/i.test(normalizedMessage)) {
    return 'Apenas uma antecipação pendente e ativa pode ser paga.';
  }

  if (/^a production advance with allocations cannot have its payment reversed\.?$/i.test(normalizedMessage)) {
    return 'Não é possível desfazer o pagamento porque esta antecipação já foi apropriada em uma viagem.';
  }

  if (/^only an active executed production advance can have its payment reversed\.?$/i.test(normalizedMessage)) {
    return 'Apenas uma antecipação paga e ativa pode ter o pagamento desfeito.';
  }

  if (/^you do not have permission to perform this action\.?$/i.test(normalizedMessage)) {
    return 'Você não tem permissão para realizar esta ação.';
  }

  if (/^unauthorized\.?$/i.test(normalizedMessage)) {
    return 'Sua sessão expirou. Faça login novamente.';
  }

  if (/^validation failed\.?$/i.test(normalizedMessage)) {
    return 'Não foi possível validar os dados enviados. Revise as informações e tente novamente.';
  }

  return undefined;
}

function toResponse<T>(response: any, successMessage: string, errorMessage: string): ProductionAdvancesProviderResponse<T> {
  const status = response?.status ?? 0;
  const success = response?.success !== false && status >= 200 && status < 300;
  const apiMessage = response?.data?.message || response?.message;
  const translatedMessage = translateKnownApiMessage(apiMessage);
  const hasDataProperty = response?.data && Object.prototype.hasOwnProperty.call(response.data, 'data');
  const payload = hasDataProperty ? response.data.data : response?.data;

  return {
    success,
    message: success ? successMessage : translatedMessage || (isTechnicalMessage(apiMessage) ? errorMessage : apiMessage),
    data: success ? payload : undefined
  };
}

function removeEmptyFilters(filter: ProductionAdvancesFilter) {
  return Object.entries(filter).reduce<Record<string, unknown>>((params, [key, value]) => {
    if (value !== "''" && value !== '' && value !== undefined && value !== null) {
      params[key] = value;
    }
    return params;
  }, {});
}

export function ProductionAdvancesProvider({ children }: ProductionAdvancesProviderProps) {
  async function getProductionAdvances(filter: ProductionAdvancesFilter = {}) {
    const response: ApiResponse = await apiV2.get(productionAdvancesRoutes.advances, { params: removeEmptyFilters(filter) });
    return toResponse<ProductionAdvance[]>(response, 'Adiantamentos consultados com sucesso!', 'Não foi possível consultar os adiantamentos de produção.');
  }

  async function getProductionAdvance(advanceId: EntityId) {
    const response: ApiResponse = await apiV2.get(productionAdvancesRoutes.advance(advanceId));
    return toResponse<ProductionAdvanceDetails>(response, 'Adiantamento consultado com sucesso!', 'Não foi possível consultar o adiantamento de produção.');
  }

  async function createProductionAdvance(advance: ProductionAdvanceInput) {
    const response = await apiV2.post(productionAdvancesRoutes.advances, advance);
    return toResponse<ProductionAdvance>(response, 'Adiantamento de produção adicionado com sucesso!', 'Não foi possível adicionar o adiantamento de produção.');
  }

  async function updateProductionAdvance(advanceId: EntityId, advance: ProductionAdvanceUpdateInput) {
    const response = await apiV2.put(productionAdvancesRoutes.advance(advanceId), advance);
    return toResponse<ProductionAdvance>(response, 'Adiantamento de produção atualizado com sucesso!', 'Não foi possível atualizar o adiantamento de produção.');
  }

  async function cancelProductionAdvance(advanceId: EntityId) {
    const response = await apiV2.delete(productionAdvancesRoutes.advance(advanceId));
    const result = toResponse<undefined>(response, 'Antecipação de produção excluída com sucesso!', 'Não foi possível excluir a antecipação de produção.');

    return result;
  }

  async function payProductionAdvance(advanceId: EntityId, payment: ProductionAdvancePaymentInput) {
    const response = await apiV2.post(productionAdvancesRoutes.pay(advanceId), payment);
    return toResponse<undefined>(response, 'Antecipação de produção paga com sucesso!', 'Não foi possível pagar a antecipação de produção.');
  }

  async function reverseProductionAdvancePayment(advanceId: EntityId) {
    const response = await apiV2.post(productionAdvancesRoutes.reversePayment(advanceId));
    return toResponse<undefined>(response, 'Pagamento da antecipação desfeito com sucesso!', 'Não foi possível desfazer o pagamento da antecipação de produção.');
  }

  return (
    <ProductionAdvancesProviderContext.Provider value={{
      getProductionAdvances,
      getProductionAdvance,
      createProductionAdvance,
      updateProductionAdvance,
      cancelProductionAdvance,
      payProductionAdvance,
      reverseProductionAdvancePayment
    }}>
      {children}
    </ProductionAdvancesProviderContext.Provider>
  );
}

export function UseProductionAdvancesProvider() {
  return useContext(ProductionAdvancesProviderContext);
}
